import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ObjectSchema, object, ref } from 'yup'

import { SignUpContract } from '@/AutoEnrollment/application'
import { SignUpRepository } from '@/AutoEnrollment/domain'
import { ApiSignUpFormErrorFieldAdapter } from '@/AutoEnrollment/infrastructure/ApiAutoEnrollmentAdapters'
import { MessagesModel } from '@/Shared/domain'
import { useAuth } from '@/Shared/ui/auth'
import {
  checkboxSchema,
  emailSchema,
  mobilePhoneSchema,
  nonEmptyStringSchema,
  requiredCheckboxSchema,
  stringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { navigationRoutes, openNavigationRoutes } from '@/Shared/ui/routers'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export interface SignUpForm {
  name: string
  surname1: string
  surname2?: string
  phone?: string
  whatsappConsent?: boolean
  username: string
  email: string
  emailRepeat: string
  password: string
  passwordRepeat: string
  acceptConditions: boolean
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<SignUpForm> =>
  object({
    name: nonEmptyStringSchema(t, 20),
    surname1: nonEmptyStringSchema(t, 20),
    surname2: stringSchema(t, 20),
    phone: mobilePhoneSchema(t),
    whatsappConsent: checkboxSchema(t),
    username: nonEmptyStringSchema(t, 50).min(
      4,
      t(formTranslation.errors.minChars, { value: 4 }),
    ),
    email: emailSchema(t, true).required(),
    emailRepeat: stringSchema(t)
      .oneOf([ref('email')], t(formTranslation.errors.emailMatch))
      .defined(),
    password: nonEmptyStringSchema(t).matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.{8,})/,
      t(formTranslation.errors.passwordFormat),
    ),
    passwordRepeat: stringSchema(t)
      .oneOf([ref('password')], t(formTranslation.errors.passwordMatch))
      .defined(),
    acceptConditions: requiredCheckboxSchema(t).required(),
  })

export interface IUseSignUpController {
  form: UseFormReturn<SignUpForm>
  save: (data: SignUpForm) => Promise<void>
}

export const useSignUpController = (
  repository: SignUpRepository,
): IUseSignUpController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const navigate = useNavigate()
  const { storeToken } = useAuth()

  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<SignUpForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
  })

  const save = async (data: SignUpForm): Promise<void> => {
    startLoading()

    const response = await SignUpContract(repository).signUp(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, form, { notifySuccess: false })

    if (valid) {
      if (response.data?.token) {
        storeToken(response.data.token)

        navigate(openNavigationRoutes.otpAuth, {
          state: {
            origin: openNavigationRoutes.signUp,
            otpGuid: response.data.otpGuid,
            otpEmail: response.data.otpEmail,
          },
        })
      } else navigate(navigationRoutes.autoEnrollment)
    } else {
      // Error management
      if (response?.meta.messages.length > 0) {
        response?.meta?.messages.forEach((errorMessage: MessagesModel) => {
          const errorField = ApiSignUpFormErrorFieldAdapter(errorMessage)
          if (errorField) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            form.setError(errorField as any, {
              type: 'value',
              message: errorMessage.description,
            })
          }
        })
      }
    }
  }

  return {
    form,
    save,
  }
}
